import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import {
  mergeLocalizedEntryValues,
  selectTrainingLocalizationState,
  type LocalizedString,
} from "../../../app/slices/TrainingLocalizationSlice";
import { setSelectedSlideId } from "../../../app/slices/GlobalSlice";
import { updateSelectedInteraction } from "../../../app/slices/TrainingSlice";
import type { RootState } from "../../../app/Store";
import {
  collectPowerSlideKeys,
  countPowerSlideTemplateKeys,
  createPowerSlide,
  IPowerSlide,
  ISlide,
  remapPowerSlide,
} from "../../../models/interactions/PowerSlideHelper";
import { PowerSlideTemplateId } from "../../../models/interactions/PowerSlideTemplates";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import {
  readPowerSlideClipboard,
  writePowerSlideClipboard,
} from "./PowerSlideClipboard";

/**
 * Every operation that changes the shape of a deck. Duplicate, copy and paste
 * all allocate fresh localization keys and copy the source's text across, so
 * two slides never share a key - editing one would otherwise silently edit the
 * other.
 */
export function usePowerSlideDeck(deck: IPowerSlide | undefined) {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { GetArrayOfKeys } = useTrainingLocalization();
  const slides = deck?.slides ?? [];

  const commit = useCallback(
    (nextSlides: ISlide[]) => {
      if (!deck) return;
      const updated: IPowerSlide = { ...deck, slides: nextSlides };
      dispatch(updateSelectedInteraction(updated));
    },
    [deck, dispatch]
  );

  /** Copies a key's text into a new key, for every language in the training. */
  const copyLocalizedEntry = useCallback(
    (originalKey: string, newKey: string) => {
      const localization = selectTrainingLocalizationState(store.getState());
      const sourceEntry = localization.localizedEntries[originalKey];
      if (!sourceEntry) return;

      const enabled = new Set(
        localization.localizedLanguages.map((language) => language.shortcode)
      );
      const entries: LocalizedString = Object.fromEntries(
        Object.entries(sourceEntry)
          .filter(([shortcode]) => enabled.has(shortcode))
          .map(([shortcode, value]) => [shortcode, { ...value }])
      );
      if (Object.keys(entries).length === 0) return;
      dispatch(mergeLocalizedEntryValues({ key: newKey, entries }));
    },
    [dispatch, store]
  );

  /** A deep copy of a slide on fresh ids and fresh keys, text carried over. */
  const cloneSlide = useCallback(
    (slide: ISlide): ISlide => {
      const oldKeys = collectPowerSlideKeys({ slides: [slide] } as IPowerSlide);
      const newKeys = GetArrayOfKeys(oldKeys.length);
      const keyMap = new Map<string, string>();
      oldKeys.forEach((oldKey, index) => {
        const newKey = newKeys[index];
        if (!newKey) return;
        keyMap.set(oldKey, newKey);
        copyLocalizedEntry(oldKey, newKey);
      });
      return remapPowerSlide(slide, keyMap);
    },
    [GetArrayOfKeys, copyLocalizedEntry]
  );

  const indexOf = useCallback(
    (slideId: string | undefined) =>
      slides.findIndex((slide) => slide.id === slideId),
    [slides]
  );

  /** Insertion point shared by Add, Duplicate and Paste: after the selection. */
  const insertAfter = useCallback(
    (slideId: string | undefined, slide: ISlide) => {
      const index = indexOf(slideId);
      const next = [...slides];
      next.splice(index < 0 ? next.length : index + 1, 0, slide);
      commit(next);
      dispatch(setSelectedSlideId(slide.id));
    },
    [commit, dispatch, indexOf, slides]
  );

  const addSlide = useCallback(
    (templateId: PowerSlideTemplateId, afterSlideId?: string) => {
      const slide = createPowerSlide(
        templateId,
        GetArrayOfKeys(countPowerSlideTemplateKeys(templateId))
      );
      insertAfter(afterSlideId, slide);
    },
    [GetArrayOfKeys, insertAfter]
  );

  const updateSlide = useCallback(
    (next: ISlide) => {
      commit(slides.map((slide) => (slide.id === next.id ? next : slide)));
    },
    [commit, slides]
  );

  const duplicateSlide = useCallback(
    (slideId: string) => {
      const source = slides.find((slide) => slide.id === slideId);
      if (!source) return;
      insertAfter(slideId, cloneSlide(source));
    },
    [cloneSlide, insertAfter, slides]
  );

  /**
   * Duplicate and Copy are both offered because they differ: Duplicate inserts
   * here, Copy puts the slide on the clipboard for another deck.
   */
  const copySlide = useCallback(
    (slideId: string) => {
      const source = slides.find((slide) => slide.id === slideId);
      if (source) writePowerSlideClipboard(source);
    },
    [slides]
  );

  /** Lands after the selected slide, so paste and duplicate behave the same. */
  const pasteSlide = useCallback(
    (afterSlideId?: string) => {
      const source = readPowerSlideClipboard();
      if (!source || !deck) return;
      insertAfter(afterSlideId, cloneSlide(source));
    },
    [cloneSlide, deck, insertAfter]
  );

  const deleteSlide = useCallback(
    (slideId: string) => {
      const index = indexOf(slideId);
      if (index < 0) return;
      const next = slides.filter((slide) => slide.id !== slideId);
      commit(next);
      // Select the slide that took its place, or the new last one.
      const successor = next[Math.min(index, next.length - 1)];
      dispatch(setSelectedSlideId(successor?.id ?? ""));
    },
    [commit, dispatch, indexOf, slides]
  );

  const moveSlide = useCallback(
    (slideId: string, toIndex: number) => {
      const from = indexOf(slideId);
      if (from < 0) return;
      const next = [...slides];
      const [moved] = next.splice(from, 1);
      // toIndex is a gap in the original list, so it shifts left once the
      // slide is lifted out from before it.
      const target = from < toIndex ? toIndex - 1 : toIndex;
      next.splice(Math.max(0, Math.min(target, next.length)), 0, moved);
      commit(next);
    },
    [commit, indexOf, slides]
  );

  return {
    addSlide,
    updateSlide,
    duplicateSlide,
    copySlide,
    pasteSlide,
    deleteSlide,
    moveSlide,
  };
}
